import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, UserCheck, FileCheck, Building2, Briefcase, AlertTriangle, Clock, CheckCircle, XCircle, Send, FileX, Timer, Eye, HelpCircle, StopCircle, Download, Receipt, RefreshCw, FileEdit, LogOut, UserCog, PauseCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Sidebar from '@/components/Sidebar';
import EmployeesTab from '@/components/hr/EmployeesTab';
import ClientCasesTab from '@/components/hr/ClientCasesTab';
import InsufficiencyTab from '@/components/agency/InsufficiencyTab';
import NotificationCenter from '@/components/shared/NotificationCenter';
import ClientInvoices from '@/components/agency/ClientInvoices';
import CandidateDraftsTabHR from '@/components/hr/CandidateDraftsTabHR';
import HRUsersTab from '@/components/hr/HRUsersTab';
import { generateDashboardPDF } from '@/utils/pdfExport';
import { formatDateDDMMYYYY } from '@/utils/textFormatting';
import { isStoppedCase } from '@/utils/caseFilters';

export default function HRDashboard({ user, onLogout, onSwitchAccount }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [caseStats, setCaseStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allCases, setAllCases] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [clientInfo, setClientInfo] = useState(null);
  const [entityInfo, setEntityInfo] = useState(null);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState('all');
  
  // Drill-down modal state
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [drillDownTitle, setDrillDownTitle] = useState('');
  const [drillDownData, setDrillDownData] = useState([]);
  const [drillDownType, setDrillDownType] = useState('cases'); // 'cases' or 'employees'

  useEffect(() => {
    fetchAllStats();
  }, []);

  // Date filter helper
  const getDateFilteredCases = (cases) => {
    if (dateFilter === 'all') return cases;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return cases.filter(c => {
      const caseDate = new Date(c.created_at);
      switch (dateFilter) {
        case 'today':
          return caseDate >= today;
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return caseDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return caseDate >= monthAgo;
        case 'year':
          const yearAgo = new Date(today);
          yearAgo.setFullYear(yearAgo.getFullYear() - 1);
          return caseDate >= yearAgo;
        default:
          return true;
      }
    });
  };

  const getDateFilteredEmployees = (employees) => {
    if (dateFilter === 'all') return employees;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return employees.filter(e => {
      const empDate = new Date(e.created_at);
      switch (dateFilter) {
        case 'today':
          return empDate >= today;
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return empDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return empDate >= monthAgo;
        case 'year':
          const yearAgo = new Date(today);
          yearAgo.setFullYear(yearAgo.getFullYear() - 1);
          return empDate >= yearAgo;
        default:
          return true;
      }
    });
  };

  const fetchAllStats = async () => {
    try {
      const [statsRes, casesRes, employeesRes] = await Promise.all([
        axios.get('/dashboard/stats'),
        axios.get('/cases'),
        axios.get('/employees')
      ]);
      
      setStats(statsRes.data);
      
      // Fetch client and entity info if user has client_id or entity_id
      if (user?.client_id) {
        try {
          const clientRes = await axios.get(`/clients/${user.client_id}`);
          setClientInfo(clientRes.data);
        } catch (e) {
          console.log('Could not fetch client info');
        }
      }
      if (user?.entity_id) {
        try {
          const entityRes = await axios.get(`/entities/${user.entity_id}`);
          setEntityInfo(entityRes.data);
        } catch (e) {
          console.log('Could not fetch entity info');
        }
      }
      
      // Calculate detailed case statistics
      const cases = casesRes.data || [];
      const employees = employeesRes.data || [];
      
      setAllCases(cases);
      setAllEmployees(employees);
      
      const now = new Date();
      const defaultTAT = 7; // days
      
      // Helper to check if case has any insufficiency (check-level or entry-level)
      const hasAnyInsufficiency = (c) => {
        // Check for check-level insufficiencies
        const hasCheckInsuff = c.checks?.some(ch => ch.has_insufficiency);
        // Check for entry-level insufficiencies
        const hasEntryInsuff = (c.entry_insufficiencies || []).some(ei => ei.status === 'open');
        // Check for has_open_insufficiency flag
        return hasCheckInsuff || hasEntryInsuff || c.has_open_insufficiency;
      };
      
      const caseStatistics = {
        totalCases: cases.length,
        completed: cases.filter(c => c.status === 'completed' || c.status === 'report_approved').length,
        newInvitesSent: employees.filter(e => e.invited === true && !e.details_filled).length,
        pendingSubmission: employees.filter(e => e.invited && e.details_filled && !e.declaration_signed).length,
        // In Progress: Only count cases that are truly in verification process
        // pending_qc cases are NOT in progress - they're waiting for QC review first
        // qc_approved with all checks still "assigned"/"pending" are NOT in progress - they're "Not Initiated"
        inProgress: cases.filter(c => {
          if (hasAnyInsufficiency(c)) return false; // Exclude insufficiency cases
          if (c.status === 'pending_qc') return false; // Exclude pending QC - QC hasn't approved yet
          if (c.status === 'in_progress') return true;
          // For qc_approved, only count if at least one check has actually started work
          if (c.status === 'qc_approved') {
            const checks = c.checks || [];
            const hasActiveCheck = checks.some(ch => 
              ['in_progress', 'vendor_assigned', 'verified', 'completed', 'clear', 'discrepancy', 'discrepant', 'failed'].includes(ch.status) ||
              ch.verification_progress === 'initiated' || ch.verification_progress === 'in_progress'
            );
            return hasActiveCheck;
          }
          return false;
        }).length,
        // Draft/Not Started: Cases that are initiated but not submitted, or basic_initiated (awaiting employee submission)
        draftCases: cases.filter(c => 
          (c.status === 'initiated' && !c.employee_submitted && c.source !== 'bulk_import' && c.source !== 'data_entry') ||
          c.status === 'basic_initiated'
        ).length,
        insufficiencyCases: cases.filter(c => hasAnyInsufficiency(c)).length,
        insufficientEmployees: employees.filter(e => e.has_insufficiency).length,
        stoppedCases: cases.filter(isStoppedCase).length,
        // TAT starts ONLY from ACTUAL SUBMISSION date - never from creation date
        // Even for bulk_import/data_entry, TAT only starts when employee_submitted=true
        withinTAT: cases.filter(c => {
          // Only count TAT for actually submitted cases
          if (!c.employee_submitted) return true; // Not submitted yet, TAT hasn't started
          
          const tatStartDate = c.submitted_at || c.updated_at || c.created_at;
          if (!tatStartDate) return true;
          
          const startDate = new Date(tatStartDate);
          const daysDiff = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
          return daysDiff <= defaultTAT;
        }).length,
        overTAT: cases.filter(c => {
          // Only count TAT for actually submitted cases
          if (!c.employee_submitted) return false; // Not submitted yet, can't be over TAT
          
          const tatStartDate = c.submitted_at || c.updated_at || c.created_at;
          if (!tatStartDate) return false;
          
          const startDate = new Date(tatStartDate);
          const daysDiff = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
          return daysDiff > defaultTAT;
        }).length,
        clearCases: cases.filter(c => {
          const checks = c.checks || [];
          return checks.length > 0 && checks.every(ch => ch.status === 'verified');
        }).length,
        discrepancyCases: cases.filter(c => {
          const checks = c.checks || [];
          return checks.some(ch => ch.status === 'failed' || ch.result === 'discrepancy');
        }).length,
        unableToVerify: cases.filter(c => {
          const checks = c.checks || [];
          return checks.some(ch => ch.status === 'unable_to_verify');
        }).length
      };
      
      setCaseStats(caseStatistics);
    } catch (error) {
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const openDrillDown = (title, data, type = 'cases') => {
    setDrillDownTitle(title);
    // Sort data by created_at date, latest first
    const sortedData = [...data].sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA; // Latest first
    });
    setDrillDownData(sortedData);
    setDrillDownType(type);
    setShowDrillDown(true);
  };

  // Function to get display status based on actual submission state
  const getDisplayStatus = (caseItem) => {
    // If case is in_progress or qc_approved, check if checks have been started
    if (['in_progress', 'qc_approved'].includes(caseItem.status)) {
      const checks = caseItem.checks || [];
      const hasAnyCheckStarted = checks.some(check => 
        check.assigned_at || check.status === 'in_progress' || check.status === 'vendor_assigned' || 
        check.status === 'verified' || check.status === 'completed' ||
        check.verification_progress === 'initiated' || check.verification_progress === 'in_progress'
      );
      if (caseItem.status === 'qc_approved' && hasAnyCheckStarted) {
        return 'in_progress';
      }
    }
    return caseItem.status;
  };

  const getStatusBadge = (status, caseItem = null) => {
    // If caseItem is provided, use corrected display status
    const displayStatus = caseItem ? getDisplayStatus(caseItem) : status;
    
    const config = {
      'pending': { class: 'bg-slate-100 text-slate-700', label: 'Pending' },
      'initiated': { class: 'bg-slate-100 text-slate-700', label: 'Pending Submission' },
      'details_filled': { class: 'bg-cyan-100 text-cyan-700', label: 'Details Filled' },
      'pending_submission': { class: 'bg-slate-100 text-slate-700', label: 'Pending Submission' },
      'pending_client_approval': { class: 'bg-indigo-100 text-indigo-700', label: 'Pending Client Approval' },
      'pending_qc': { class: 'bg-amber-100 text-amber-700', label: 'Pending QC' },
      'qc_approved': { class: 'bg-emerald-100 text-emerald-700', label: 'QC Approved' },
      'in_progress': { class: 'bg-blue-100 text-blue-700', label: 'In Progress' },
      'completed': { class: 'bg-emerald-100 text-emerald-700', label: 'Completed' },
      'verified': { class: 'bg-emerald-100 text-emerald-700', label: 'Verified' },
      'failed': { class: 'bg-rose-100 text-rose-700', label: 'Failed' },
      'unable_to_verify': { class: 'bg-amber-100 text-amber-700', label: 'Unable to Verify' },
      'final_report_qc': { class: 'bg-purple-100 text-purple-700', label: 'Final Report QC' },
      'report_approved': { class: 'bg-emerald-100 text-emerald-700', label: 'Report Approved' },
      'invited': { class: 'bg-blue-100 text-blue-700', label: 'Invited' },
      'stopped': { class: 'bg-red-100 text-red-700', label: 'Stopped' },
      'on_hold': { class: 'bg-rose-100 text-rose-700', label: 'On Hold' },
      'insufficiency': { class: 'bg-amber-100 text-amber-700', label: 'Insufficiency' }
    };
    const c = config[displayStatus] || config['pending'];
    return <Badge className={c.class}>{c.label}</Badge>;
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Briefcase },
    { id: 'employees', label: 'Employees', icon: Users },
    { id: 'candidate-drafts', label: 'Candidate Drafts', icon: FileEdit },
    { id: 'cases', label: 'Verification Cases', icon: FileCheck },
    { id: 'stopped-cases', label: 'Stopped Cases', icon: StopCircle },
    { id: 'insufficiencies', label: 'Insufficiencies', icon: AlertTriangle },
    { id: 'invoices', label: 'Invoices', icon: Receipt },
    { id: 'users', label: 'Manage Users', icon: UserCog },
  ];

  // Stopped cases filters
  const [stoppedReasonFilter, setStoppedReasonFilter] = useState('all');
  const [stoppedDateFrom, setStoppedDateFrom] = useState('');
  const [stoppedDateTo, setStoppedDateTo] = useState('');

  const stoppedCases = allCases.filter(isStoppedCase);

  const getFilteredStoppedCases = () => {
    let filtered = stoppedCases;
    
    if (stoppedReasonFilter !== 'all') {
      filtered = filtered.filter(c => c.stop_reason === stoppedReasonFilter);
    }
    
    if (stoppedDateFrom) {
      filtered = filtered.filter(c => new Date(c.stopped_at || c.updated_at) >= new Date(stoppedDateFrom));
    }
    
    if (stoppedDateTo) {
      filtered = filtered.filter(c => new Date(c.stopped_at || c.updated_at) <= new Date(stoppedDateTo + 'T23:59:59'));
    }
    
    return filtered;
  };

  const getStopReasonLabel = (reason) => {
    const labels = {
      'candidate_no_show': 'Candidate No Show',
      'terminated': 'Terminated',
      'candidate_withdrew': 'Candidate Withdrew',
      'offer_revoked': 'Offer Revoked',
      'other': 'Other'
    };
    return labels[reason] || reason || 'N/A';
  };

  const StatCard = ({ title, value, icon: Icon, color = 'slate', subtitle, onClick }) => {
    const colorClasses = {
      slate: 'border-slate-200 bg-white',
      blue: 'border-blue-200 bg-blue-50',
      emerald: 'border-emerald-200 bg-emerald-50',
      amber: 'border-amber-200 bg-amber-50',
      rose: 'border-rose-200 bg-rose-50',
      purple: 'border-purple-200 bg-purple-50',
      cyan: 'border-cyan-200 bg-cyan-50'
    };
    const textClasses = {
      slate: 'text-slate-600',
      blue: 'text-blue-600',
      emerald: 'text-emerald-600',
      amber: 'text-amber-600',
      rose: 'text-rose-600',
      purple: 'text-purple-600',
      cyan: 'text-cyan-600'
    };
    const valueClasses = {
      slate: 'text-slate-900',
      blue: 'text-blue-700',
      emerald: 'text-emerald-700',
      amber: 'text-amber-700',
      rose: 'text-rose-700',
      purple: 'text-purple-700',
      cyan: 'text-cyan-700'
    };
    const iconClasses = {
      slate: 'text-slate-400',
      blue: 'text-blue-400',
      emerald: 'text-emerald-400',
      amber: 'text-amber-400',
      rose: 'text-rose-400',
      purple: 'text-purple-400',
      cyan: 'text-cyan-400'
    };

    return (
      <Card 
        className={`${colorClasses[color]} shadow-sm hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
        onClick={onClick}
        data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div>
              <p className={`text-sm font-medium ${textClasses[color]}`}>{title}</p>
              <p className={`text-3xl font-bold ${valueClasses[color]} mt-1`}>{value}</p>
              {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
              {onClick && <p className="text-xs text-slate-400 mt-1">Click to view details</p>}
            </div>
            <Icon className={`w-8 h-8 ${iconClasses[color]}`} />
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderDashboard = () => {
    if (loading) {
      return <div className="text-slate-600">Loading statistics...</div>;
    }

    // Apply date filter
    const filteredCases = getDateFilteredCases(allCases);
    const filteredEmployees = getDateFilteredEmployees(allEmployees);

    // Helper functions for filtering
    const now = new Date();
    const defaultTAT = 7;
    
    // Helper to check if case has any insufficiency (check-level or entry-level)
    const hasAnyInsufficiency = (c) => {
      const hasCheckInsuff = c.checks?.some(ch => ch.has_insufficiency);
      const hasEntryInsuff = (c.entry_insufficiencies || []).some(ei => ei.status === 'open');
      return hasCheckInsuff || hasEntryInsuff || c.has_open_insufficiency;
    };
    
    const getCompletedCases = () => filteredCases.filter(c => c.status === 'completed' || c.status === 'report_approved');
    // In Progress: Only cases truly in verification (QC approved and work started)
    // pending_qc cases are NOT in progress - they're waiting for QC review
    const getInProgressCases = () => filteredCases.filter(c => {
      if (hasAnyInsufficiency(c)) return false;
      if (c.status === 'pending_client_approval') return false;
      if (c.status === 'pending_qc') return false;
      if (c.status === 'in_progress') return true;
      if (c.status === 'qc_approved') {
        // Only count as in progress if at least one check has actually started
        const checks = c.checks || [];
        return checks.some(ch => 
          ['in_progress', 'verified', 'completed', 'clear', 'discrepancy', 'discrepant', 'failed'].includes(ch.status)
        );
      }
      return false;
    });
    // Pending Client Approval cases
    const getPendingApprovalCases = () => filteredCases.filter(c => c.status === 'pending_client_approval');
    // Draft cases: Initiated but not submitted, or basic_initiated (awaiting employee submission)
    const getDraftCases = () => filteredCases.filter(c => 
      (c.status === 'initiated' && !c.employee_submitted && c.source !== 'bulk_import' && c.source !== 'data_entry') ||
      c.status === 'basic_initiated'
    );
    // Not Initiated: QC approved but verification hasn't started
    const getNotInitiatedCases = () => filteredCases.filter(c => {
      const isSubmitted = c.employee_submitted || c.source === 'bulk_import' || c.source === 'data_entry';
      if (!isSubmitted) return false;  // Draft cases excluded
      if (hasAnyInsufficiency(c)) return false;  // Cases with insufficiency excluded
      if (c.status === 'qc_approved') {
        // Check if any check has been assigned/started
        const checks = c.checks || [];
        const hasAnyStarted = checks.some(check => 
          check.status === 'in_progress' || check.status === 'verified' || check.status === 'completed'
        );
        return !hasAnyStarted;  // Not initiated if no check has started
      }
      return false;
    });
    const getInsufficientCases = () => filteredCases.filter(c => hasAnyInsufficiency(c));
    const getStoppedCases = () => filteredCases.filter(isStoppedCase);
    const getInsufficientEmployees = () => filteredEmployees.filter(e => e.has_insufficiency);
    const getPendingQCCases = () => filteredCases.filter(c => c.status === 'pending_qc');
    const getFinalReportQCCases = () => filteredCases.filter(c => c.status === 'final_report_qc');
    // TAT starts from SUBMISSION date, not creation date
    const getWithinTATCases = () => filteredCases.filter(c => {
      const isSubmitted = c.employee_submitted || c.source === 'bulk_import' || c.source === 'data_entry';
      if (!isSubmitted) return true; // Not submitted yet
      
      const tatStartDate = c.submitted_at || c.created_at;
      if (!tatStartDate) return true;
      
      const startDate = new Date(tatStartDate);
      const daysDiff = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
      return daysDiff <= defaultTAT;
    });
    const getOverTATCases = () => filteredCases.filter(c => {
      const isSubmitted = c.employee_submitted || c.source === 'bulk_import' || c.source === 'data_entry';
      if (!isSubmitted) return false; // Not submitted yet
      
      const tatStartDate = c.submitted_at || c.created_at;
      if (!tatStartDate) return false;
      
      const startDate = new Date(tatStartDate);
      const daysDiff = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
      return daysDiff > defaultTAT;
    });
    const getClearCases = () => filteredCases.filter(c => {
      const checks = c.checks || [];
      return checks.length > 0 && checks.every(ch => ch.status === 'verified');
    });
    const getDiscrepancyCases = () => filteredCases.filter(c => {
      const checks = c.checks || [];
      return checks.some(ch => ch.status === 'failed' || ch.result === 'discrepancy');
    });
    const getUnableToVerifyCases = () => filteredCases.filter(c => {
      const checks = c.checks || [];
      return checks.some(ch => ch.status === 'unable_to_verify');
    });
    const getInvitedEmployees = () => filteredEmployees.filter(e => e.invited === true && !e.details_filled);
    const getPendingSubmissionEmployees = () => filteredEmployees.filter(e => e.invited && e.details_filled && !e.declaration_signed);

    // Calculate stats based on filtered data
    const stats = {
      totalCases: filteredCases.length,
      completed: getCompletedCases().length,
      newInvitesSent: getInvitedEmployees().length,
      pendingSubmission: getPendingSubmissionEmployees().length,
      notInitiated: getNotInitiatedCases().length,
      inProgress: getInProgressCases().length,
      pendingQC: getPendingQCCases().length,
      finalReportQC: getFinalReportQCCases().length,
      insufficiencyCases: getInsufficientCases().length,
      pendingApproval: getPendingApprovalCases().length,
      stoppedCases: getStoppedCases().length,
      withinTAT: getWithinTATCases().length,
      overTAT: getOverTATCases().length,
      clearCases: getClearCases().length,
      discrepancyCases: getDiscrepancyCases().length,
      unableToVerify: getUnableToVerifyCases().length
    };

    const dateFilterLabel = {
      'all': 'All Time',
      'today': 'Today',
      'week': 'This Week',
      'month': 'This Month',
      'year': 'This Year'
    };

    const handleExportPDF = () => {
      try {
        generateDashboardPDF({
          title: 'Client Dashboard Report',
          subtitle: user.name,
          dateRange: dateFilterLabel[dateFilter],
          generatedBy: user.name,
          stats: [
            { label: 'Total Cases', value: stats.totalCases, color: 'slate' },
            { label: 'Completed', value: stats.completed, color: 'emerald' },
            { label: 'Pending QC', value: stats.pendingQC, color: 'amber' },
            { label: 'Final Report QC', value: stats.finalReportQC, color: 'purple' },
            { label: 'In Progress', value: stats.inProgress, color: 'blue' },
            { label: 'Insufficiency', value: stats.insufficiencyCases, color: 'amber' },
            { label: 'Within TAT', value: stats.withinTAT, color: 'emerald' },
            { label: 'Over TAT', value: stats.overTAT, color: 'rose' },
            { label: 'Clear Cases', value: stats.clearCases, color: 'emerald' },
            { label: 'Discrepancy', value: stats.discrepancyCases, color: 'rose' },
            { label: 'Unable to Verify', value: stats.unableToVerify, color: 'amber' }
          ],
          tables: [
            {
              title: 'Recent Cases',
              headers: ['Case ID', 'Employee', 'Status', 'Created'],
              rows: filteredCases.slice(0, 20).map(c => [
                c.id || '-',
                c.employee_name || '-',
                c.status || '-',
                c.created_at ? formatDateDDMMYYYY(c.created_at) : '-'
              ])
            }
          ]
        });
        toast.success('PDF report downloaded successfully');
      } catch (error) {
        toast.error('Failed to generate PDF');
        console.error('PDF generation error:', error);
      }
    };

    return (
      <div>
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Client Dashboard</h1>
            <p className="text-slate-600">Welcome back, {user.name} - Overview of your verification cases</p>
            {(clientInfo || entityInfo) && (
              <div className="flex items-center gap-4 mt-2 text-sm">
                {clientInfo && (
                  <span className="flex items-center gap-1.5 text-slate-700">
                    <Building2 className="w-4 h-4 text-blue-600" />
                    <span className="font-medium">{clientInfo.company_name || clientInfo.name}</span>
                  </span>
                )}
                {entityInfo && (
                  <span className="flex items-center gap-1.5 text-slate-600">
                    <Briefcase className="w-4 h-4 text-purple-600" />
                    <span>{entityInfo.entity_name || entityInfo.name}</span>
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={handleExportPDF}
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              data-testid="export-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-600">Filter:</Label>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-36" data-testid="date-filter-select">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="year">This Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {dateFilter !== 'all' && (
          <div className="mb-4 p-2 bg-blue-50 rounded-lg text-sm text-blue-700">
            Showing data for: <strong>{dateFilterLabel[dateFilter]}</strong>
          </div>
        )}

        {/* Primary Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard 
            title="Total Cases" 
            value={stats.totalCases} 
            icon={FileCheck} 
            color="slate"
            onClick={() => openDrillDown('All Cases', filteredCases, 'cases')}
          />
          <StatCard 
            title="Cases Completed" 
            value={stats.completed} 
            icon={CheckCircle} 
            color="emerald"
            onClick={() => openDrillDown('Completed Cases', getCompletedCases(), 'cases')}
          />
          <StatCard 
            title="Pending QC" 
            value={stats.pendingQC} 
            icon={Clock} 
            color="amber"
            onClick={() => openDrillDown('Pending QC Cases', getPendingQCCases(), 'cases')}
          />
          <StatCard 
            title="Final Report QC" 
            value={stats.finalReportQC} 
            icon={FileCheck} 
            color="purple"
            onClick={() => openDrillDown('Final Report QC Cases', getFinalReportQCCases(), 'cases')}
          />
          <StatCard 
            title="In Progress" 
            value={stats.inProgress} 
            icon={Timer} 
            color="blue"
            onClick={() => openDrillDown('In Progress Cases', getInProgressCases(), 'cases')}
          />
        </div>

        {/* Secondary Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard 
            title="Not Initiated" 
            value={stats.notInitiated} 
            icon={PauseCircle} 
            color="violet"
            subtitle="Pending verification start"
            onClick={() => openDrillDown('Not Initiated Cases', getNotInitiatedCases(), 'cases')}
          />
          <StatCard 
            title="Insufficiency Cases" 
            value={stats.insufficiencyCases} 
            icon={AlertTriangle} 
            color="amber"
            subtitle="Requires attention"
            onClick={() => openDrillDown('Insufficiency Cases', getInsufficientCases(), 'cases')}
          />
          <StatCard 
            title="Approval Pending" 
            value={stats.pendingApproval} 
            icon={Clock} 
            color="indigo"
            subtitle="Awaiting your approval"
            onClick={() => openDrillDown('Approval Pending', getPendingApprovalCases(), 'cases')}
            data-testid="pending-approval-card"
          />
          <StatCard 
            title="Stopped Cases" 
            value={stats.stoppedCases} 
            icon={StopCircle} 
            color="rose"
            subtitle="Verification stopped"
            onClick={() => openDrillDown('Stopped Cases', getStoppedCases(), 'cases')}
          />
          {/* Employee Status Summary Card */}
          <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-blue-50 to-slate-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-slate-700">Employee Status</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div 
                  className="flex justify-between p-2 bg-blue-100/50 rounded cursor-pointer hover:bg-blue-100"
                  onClick={() => openDrillDown('Invites Sent', getInvitedEmployees(), 'employees')}
                >
                  <span className="text-blue-700">Invites Sent</span>
                  <span className="font-bold text-blue-700">{stats.newInvitesSent}</span>
                </div>
                <div 
                  className="flex justify-between p-2 bg-amber-100/50 rounded cursor-pointer hover:bg-amber-100"
                  onClick={() => openDrillDown('Pending Submission', getPendingSubmissionEmployees(), 'employees')}
                >
                  <span className="text-amber-700">Pending</span>
                  <span className="font-bold text-amber-700">{stats.pendingSubmission}</span>
                </div>
                <div 
                  className="flex justify-between p-2 bg-indigo-100/50 rounded cursor-pointer hover:bg-indigo-100"
                  onClick={() => openDrillDown('In Progress Cases', getInProgressCases(), 'cases')}
                >
                  <span className="text-indigo-700">In Progress</span>
                  <span className="font-bold text-indigo-700">{stats.inProgress}</span>
                </div>
                <div 
                  className="flex justify-between p-2 bg-emerald-100/50 rounded cursor-pointer hover:bg-emerald-100"
                  onClick={() => openDrillDown('Verification Complete', getCompletedCases(), 'cases')}
                >
                  <span className="text-emerald-700">Complete</span>
                  <span className="font-bold text-emerald-700">{stats.completed}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Verification Results Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Results Overview */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Verification Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div 
                  className="p-4 bg-emerald-50 rounded-lg text-center cursor-pointer hover:bg-emerald-100 transition-colors"
                  onClick={() => openDrillDown('Clear Cases', getClearCases(), 'cases')}
                  data-testid="clear-cases-card"
                >
                  <p className="text-3xl font-bold text-emerald-700">{stats.clearCases}</p>
                  <p className="text-sm text-emerald-600 mt-1">Clear</p>
                  <p className="text-xs text-slate-500">All checks verified</p>
                </div>
                <div 
                  className="p-4 bg-rose-50 rounded-lg text-center cursor-pointer hover:bg-rose-100 transition-colors"
                  onClick={() => openDrillDown('Discrepancy Cases', getDiscrepancyCases(), 'cases')}
                  data-testid="discrepancy-cases-card"
                >
                  <p className="text-3xl font-bold text-rose-700">{stats.discrepancyCases}</p>
                  <p className="text-sm text-rose-600 mt-1">Discrepancy</p>
                  <p className="text-xs text-slate-500">Issues found</p>
                </div>
                <div 
                  className="p-4 bg-amber-50 rounded-lg text-center cursor-pointer hover:bg-amber-100 transition-colors"
                  onClick={() => openDrillDown('Unable to Verify Cases', getUnableToVerifyCases(), 'cases')}
                  data-testid="unable-to-verify-card"
                >
                  <p className="text-3xl font-bold text-amber-700">{stats.unableToVerify}</p>
                  <p className="text-sm text-amber-600 mt-1">Unable to Verify</p>
                  <p className="text-xs text-slate-500">AMBER status</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* TAT Overview */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5" />
                TAT Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div 
                  className="p-4 bg-emerald-50 rounded-lg text-center cursor-pointer hover:bg-emerald-100 transition-colors"
                  onClick={() => openDrillDown('Within TAT', getWithinTATCases(), 'cases')}
                >
                  <p className="text-3xl font-bold text-emerald-700">{stats.withinTAT}</p>
                  <p className="text-sm text-emerald-600 mt-1">Within TAT</p>
                  <p className="text-xs text-slate-500">On schedule</p>
                </div>
                <div 
                  className="p-4 bg-rose-50 rounded-lg text-center cursor-pointer hover:bg-rose-100 transition-colors"
                  onClick={() => openDrillDown('Over TAT', getOverTATCases(), 'cases')}
                >
                  <p className="text-3xl font-bold text-rose-700">{stats.overTAT}</p>
                  <p className="text-sm text-rose-600 mt-1">Over TAT</p>
                  <p className="text-xs text-slate-500">Needs attention</p>
                </div>
              </div>
              {stats.totalCases > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">TAT Compliance Rate</span>
                    <span className={`font-bold ${
                      (stats.withinTAT / stats.totalCases * 100) >= 80 ? 'text-emerald-600' : 'text-amber-600'
                    }`}>
                      {Math.round(stats.withinTAT / stats.totalCases * 100)}%
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (activeTab === 'dashboard') return renderDashboard();
    if (activeTab === 'employees') return <EmployeesTab user={user} />;
    if (activeTab === 'candidate-drafts') return <CandidateDraftsTabHR />;
    if (activeTab === 'cases') return <ClientCasesTab />;
    if (activeTab === 'stopped-cases') return renderStoppedCases();
    if (activeTab === 'insufficiencies') return <InsufficiencyTab />;
    if (activeTab === 'invoices') return <ClientInvoices isClientView={true} clientId={user?.client_id} />;
    if (activeTab === 'users') return <HRUsersTab clientId={user?.client_id} />;
  };

  const renderStoppedCases = () => {
    const filteredStoppedCases = getFilteredStoppedCases();
    
    return (
      <div>
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Stopped Cases</h1>
            <p className="text-slate-600">View all stopped verification cases for your organization</p>
          </div>
          <Badge className="bg-red-100 text-red-700 text-lg px-4 py-2">
            {stoppedCases.length} Total Stopped
          </Badge>
        </div>

        {/* Filters */}
        <Card className="border-slate-200 shadow-sm mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm text-slate-600">Stop Reason</Label>
                <Select value={stoppedReasonFilter} onValueChange={setStoppedReasonFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Reasons" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Reasons</SelectItem>
                    <SelectItem value="candidate_no_show">Candidate No Show</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                    <SelectItem value="candidate_withdrew">Candidate Withdrew</SelectItem>
                    <SelectItem value="offer_revoked">Offer Revoked</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-slate-600">From Date</Label>
                <Input 
                  type="date" 
                  value={stoppedDateFrom} 
                  onChange={(e) => setStoppedDateFrom(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">To Date</Label>
                <Input 
                  type="date" 
                  value={stoppedDateTo} 
                  onChange={(e) => setStoppedDateTo(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  onClick={() => { setStoppedReasonFilter('all'); setStoppedDateFrom(''); setStoppedDateTo(''); }}
                  className="w-full"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stopped Cases Table */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <StopCircle className="w-5 h-5 text-red-600" />
              Stopped Cases ({filteredStoppedCases.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Case ID</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Stop Reason</TableHead>
                  <TableHead>Stopped At</TableHead>
                  <TableHead>Checks Stopped</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStoppedCases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                      <StopCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>No stopped cases found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStoppedCases.map((caseItem) => {
                    const stoppedChecks = (caseItem.checks || []).filter(c => c.status === 'stop_check').length;
                    return (
                      <TableRow key={caseItem.id} className="hover:bg-slate-50">
                        <TableCell className="font-mono text-sm">{caseItem.id}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{caseItem.employee_name || 'N/A'}</p>
                            <p className="text-xs text-slate-500">{caseItem.employee_email || ''}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-red-100 text-red-700">
                            {getStopReasonLabel(caseItem.stop_reason)}
                          </Badge>
                          {caseItem.stop_description && (
                            <p className="text-xs text-slate-500 mt-1 max-w-[200px] truncate" title={caseItem.stop_description}>
                              {caseItem.stop_description}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {caseItem.stopped_at ? new Date(caseItem.stopped_at).toLocaleString() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-slate-100 text-slate-700">{stoppedChecks} checks</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar 
        menuItems={menuItems} 
        activeTab={activeTab} 
        setActiveTab={setActiveTab}
        user={user}
        onLogout={onLogout} 
        onSwitchAccount={onSwitchAccount}
        hideUserSection={true}
      />
      <main className="flex-1 overflow-auto">
        {/* Header with User Info, Notification & Logout */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-8 py-3 flex items-center justify-end gap-4">
          <span className="text-sm text-slate-600">{user.name}</span>
          <NotificationCenter />
          <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-button">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-8 lg:p-10">
          {renderContent()}
        </div>
      </main>

      {/* Drill-Down Modal */}
      <Dialog open={showDrillDown} onOpenChange={setShowDrillDown}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {drillDownTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {drillDownData.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileCheck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No data found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    {drillDownType === 'cases' ? (
                      <>
                        <TableHead>Case ID</TableHead>
                        <TableHead>Employee</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Report</TableHead>
                      </>
                    ) : (
                      <>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Invited</TableHead>
                        <TableHead>Submitted</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drillDownData.map((item) => (
                    <TableRow key={item.id} className="hover:bg-slate-50">
                      {drillDownType === 'cases' ? (
                        <>
                          <TableCell className="font-mono text-sm">{item.id || '-'}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{item.employee_name || '-'}</p>
                              <p className="text-xs text-slate-500">{item.employee_email || ''}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{item.client_name || '-'}</p>
                              <p className="text-xs text-slate-500">{item.entity_name || ''}</p>
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(item.status)}</TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {item.submitted_at
                              ? formatDateDDMMYYYY(item.submitted_at)
                              : ((item.employee_submitted || item.source === 'bulk_import' || item.source === 'data_entry')
                                  ? (item.created_at ? formatDateDDMMYYYY(item.created_at) : '-')
                                  : <span className="text-slate-400">Pending</span>)
                            }
                          </TableCell>
                          <TableCell>
                            <Button variant="outline" size="sm" data-testid={`download-report-${item.id}`} onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                toast.info('Generating BGV Report...');
                                const response = await axios.post(`/cases/${item.id}/generate-bgv-report`, { is_final: false }, { responseType: 'blob' });
                                const blob = new Blob([response.data], { type: 'application/pdf' });
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                const disposition = response.headers['content-disposition'];
                                const match = disposition?.match(/filename="?([^"]+)"?/);
                                link.download = match ? match[1] : `BGV_Report_${item.id}_${new Date().toISOString().slice(0,10)}.pdf`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                                toast.success('Report downloaded');
                              } catch { toast.error('Failed to generate report'); }
                            }}>
                              <Download className="w-3 h-3 mr-1" />PDF
                            </Button>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="font-medium">{item.name || '-'}</TableCell>
                          <TableCell className="text-sm text-slate-600">{item.email || '-'}</TableCell>
                          <TableCell className="text-sm text-slate-600">{item.phone || '-'}</TableCell>
                          <TableCell>
                            <Badge className={item.invited ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                              {item.invited ? 'Yes' : 'No'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={item.declaration_signed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                              {item.declaration_signed ? 'Yes' : 'Pending'}
                            </Badge>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDrillDown(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
